import { is, List } from "immutable";
import * as QueryString from "querystringify";
import * as React from "react";
import * as BS from "react-bootstrap";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import { AbstractSummary } from "../../../containers/abstracts/components";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { ReactHelper, Utility } from "../../../utils";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultStatePropsMap(state, ownProps),
    abstracts: state.adminAbstracts.abstracts,
    loadingAbstract: state.adminAbstracts.loadingAbstract,
    loadingType: state.adminAbstracts.loadingType,
    types: state.adminAbstracts.types,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadAbstract: (guid: string) => dispatch(Actions.AdminAbstract.loadAbstract(guid)),
    loadType: (typeId: number) => dispatch(Actions.AdminAbstract.loadType(typeId)),
    removeAbstract: (abstract: M.Abstract.Abstract) => dispatch(Actions.AdminAbstract.removeAbstract(abstract)),
    subscribeToMediaProcessing: (mediaId: number) => dispatch(Actions.DBSubs.subscribeToMediaProcessing(mediaId)),
    toggleAbstractLoading: (guid: string, loading: boolean) => dispatch(Actions.AdminAbstract.toggleLoadingAbstract(guid, loading)),
    toggleTypeLoading: (typeId: number, loading: boolean) => dispatch(Actions.AdminAbstract.toggleLoadingType(typeId, loading)),
});

type OwnProps = RouteComponentProps<{
    guid: string;
}> & {
    eventId: number;
};

type ConnectedState = Comp.Base.DefaultConnectedState & {
    abstracts: List<M.Abstract.Abstract>;
    loadingAbstract: M.Generic.ItemLoadingState;
    loadingType: M.Generic.ItemLoadingState;
    types: List<M.Abstract.AbstractType>;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadAbstract: (guid: string) => void;
    loadType: (typeId: number) => void;
    removeAbstract: (abstract: M.Abstract.Abstract) => void;
    subscribeToMediaProcessing: (mediaId: number) => void;
    toggleAbstractLoading: (guid: string, loading: boolean) => void;
    toggleTypeLoading: (typeId: number, loading: boolean) => void;
};

interface OwnState {
    abstract?: M.Abstract.Abstract;
    type?: M.Abstract.AbstractType;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class DetailsComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    private _abstractTypeId = 0;
    private _confirmDelete: Comp.Modal.ConfirmModal | null = null;

    public constructor(props: any) {
        super(props);
        this.state = {
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        this.initializeComponent();
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        if (!is(prevProps.abstracts, this.props.abstracts))
            this.loadAbstractFromState();

        if (!is(prevProps.types, this.props.types))
            this.loadTypeFromState();

        // just in case a different guid in URL has been detected
        if (this.getGuid(prevProps) !== this.getGuid()) {
            this.clearState(() => {
                this.initializeComponent();
            });
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const guid = this.getGuid();
        const eventId = this.state.abstract ? this.state.abstract.eventId : 0;
        const loading = this.state.loading || !!this.props.loadingAbstract[guid] || !!this.props.loadingType[this._abstractTypeId];

        const summary = this.state.abstract && this.state.type
            ? <AbstractSummary
                abstract={this.state.abstract}
                allowEdits
                allowUploads={this.state.type.typeMedia.length > 0}
                hideAuthors={this.isAuthorsHidden()}
                isAdmin={true}
                type={this.state.type}
            />
            : null;

        const actionsPrimary = <BS.ButtonGroup>
            <BS.Button bsStyle="default" onClick={() => this.toggleDeleteConfirmation()}>
                <Comp.Generic.Icon icon="times fa-lg" /> Delete
            </BS.Button>
            <BS.Button bsStyle="default" disabled onClick={() => {}}>
                Export
            </BS.Button>
        </BS.ButtonGroup>;

        const noAuthors = this.isAuthorsHidden() ? "?noAuthors=1" : "";
        const abstractListLink = this.lastPathIsFromListView()
            ? <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); window.history.back(); }}>Abstracts</a>
            : <Link to={Paths.AdminAbstractList(eventId) + noAuthors}>Abstracts</Link>;

        return <Comp.Page.AdminPage actionsPrimary={actionsPrimary} title="Abstracts">
            <Comp.Generic.LoadingSpinner show={loading} />

            <Comp.Modal.ConfirmModal
                headerClassName="warning"
                onProceed={() => this.handleAbstractDelete()}
                title="Delete abstract"
                ref={(ref) => this._confirmDelete = ref}
            >
                <p>Are you sure you want to delete this abstract?</p>
            </Comp.Modal.ConfirmModal>

            {this.getErrorAlert()}

            <h1>
                {abstractListLink} &raquo;{" "}
                <span dangerouslySetInnerHTML={{ __html: this.state.abstract ? this.state.abstract.title : "Details" }} />
            </h1>

            {summary}

        </Comp.Page.AdminPage>;
    }

    private clearState(callback?: () => void) {
        this.setState({
            abstract: undefined,
            type: undefined,
        }, callback);
    }

    private getGuid(nextProps?: Readonly<CombinedProps>) {
        return nextProps ? nextProps.match.params.guid : this.props.match.params.guid;
    }

    private async handleAbstractDelete() {
        if (this._confirmDelete === null)
            return;

        const abstract = this._confirmDelete.getRef() as M.Abstract.Abstract;
        if (abstract !== undefined) {
            const result = await this.props.api.delete(Api.endpoints.admin.abstract.abstract.withUrlParams({ guid: abstract.guid }));
            if (!this._isMounted)
                return;

            if (this._confirmDelete)
                this._confirmDelete.close();

            if (result.isSuccess) {
                this.props.removeAbstract(abstract);
                this.props.notifySuccess("Abstract has been successfully deleted.");
                this.props.goto(Paths.AdminAbstractList(abstract.eventId));
            }
            else {
                this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
            }
        }
    }

    private async initializeComponent() {

        // first get type ID by abstract GUID
        const guid = this.getGuid();

        // first check if we can get abstract type ID from state
        const _abstract = this.props.abstracts.find((x: M.Abstract.Abstract) => x.guid.toLowerCase() === guid.toLowerCase());
        if (_abstract) {
            this._abstractTypeId = _abstract.typeId;
            this.props.loadType(this._abstractTypeId);
        }
        else {
            // if not, get it from api
            this.toggleLoading(true);
            const typeIdResult = await this.props.api.get(Api.endpoints.abstract.typeIdByAbstractGuid.withUrlParams({ guid }));

            if (!this._isMounted)
                return;

            this.toggleLoading(false);

            if (!typeIdResult.isSuccess) {
                this.setErrors(M.Result.Result.errorsOrMessage(typeIdResult));
                return;
            }

            this._abstractTypeId = typeIdResult.data || 0;
            this.props.loadType(this._abstractTypeId);
        }

        this.loadDataFromState();
        this.props.loadAbstract(guid);
    }

    private isAuthorsHidden() {
        return QueryString.parse(this.props.location.search)["noAuthors"] === "1";
    }

    private lastPathIsFromListView() {
        return this.props.lastPath === `${Paths.AdminAbstracts.BasePath}/${this.props.eventId}`
            || (this.props.lastPath || "").indexOf(`${Paths.AdminAbstracts.BasePath}/${this.props.eventId}?`) === 0;
    }

    private async listenForMediaNotifications(abstract: M.Abstract.Abstract) {
        for (const m of abstract.media.filter((x) => x.isProcessing)) {
            this.props.subscribeToMediaProcessing(m.id);
            await Utility.delay(125);
        }
    }

    private async loadDataFromState() {
        const guid = this.getGuid();
        let abstract: M.Abstract.Abstract | undefined;
        let type: M.Abstract.AbstractType | undefined;

        if (this.props.abstracts.size) {
            abstract = this.props.abstracts.find((x: M.Abstract.Abstract) => x.guid.toLowerCase() === guid.toLowerCase());
        }

        if (this.props.types.size)
            type = this.props.types.find((x: M.Abstract.AbstractType) => x.id === this._abstractTypeId);

        await this.setStateAsync({
            ...this.state,
            abstract,
            type,
        });

        // toggle loading of data after state set
        if (!abstract)
            this.props.toggleAbstractLoading(guid, true);
        if (!type)
            this.props.toggleTypeLoading(this._abstractTypeId, true);

        // make sure event IDs match
        if (abstract && abstract.eventId !== this.props.eventId)
            this.props.goto(Paths.AdminAbstractDetails(abstract.eventId, abstract.guid.toLowerCase()));
    }

    private async loadAbstractFromState() {
        if (this.props.abstracts.size) {
            const guid = this.getGuid();
            const abstract = this.props.abstracts.find((x: M.Abstract.Abstract) => x.guid.toLowerCase() === guid.toLowerCase());
            if (abstract) {
                this.listenForMediaNotifications(abstract);
                await this.setStateAsync({
                    ...this.state,
                    abstract,
                });

                // make sure event IDs match
                if (abstract.eventId !== this.props.eventId)
                    this.props.goto(Paths.AdminAbstractDetails(abstract.eventId, abstract.guid.toLowerCase()));
            }
        }
    }

    private loadTypeFromState() {
        if (this.props.types.size) {
            const type = this.props.types.find((x: M.Abstract.AbstractType) => x.id === this._abstractTypeId);
            if (type) {
                this.setState({
                    ...this.state,
                    type,
                });
            }
        }
    }

    private toggleDeleteConfirmation() {
        if (this._confirmDelete !== null && this.state.abstract)
            this._confirmDelete.show(this.state.abstract);
    }
}

export const Details: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(DetailsComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/adminAbstracts/details/Details.tsx